'use client'

import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { PlusIcon } from 'lucide-react'

import { Button, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, toast } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import { getStaticAssetUrl } from '@helper/utils/file'
import cn from '@config/tailwind/cn'

import { useSharedTRPC } from '../../../context'
import { MyBookCollectionForm } from '../../../forms'

interface EmptyProps {
  type: 'web' | 'school'
  className?: string
}

export function Empty({ type, className }: EmptyProps) {
  const queryClient = useQueryClient()

  const trpc = useSharedTRPC()

  const [openMakeNewBookCollectionDialog, setOpenMakeNewBookCollectionDialog] = useState(false)

  return (
    <>
      <div
        className={cn(
          'srd:w-full srd:flex srd:flex-col srd:items-center srd:justify-center srd:gap-y-6 srd:h-fit srd:min-h-[70dvh] srd:max-w-[800px] srd:mx-auto srd:text-neutrals-white',
          className,
        )}
      >
        <img src={getStaticAssetUrl('illustration/foam.png')} alt='' width={60} height={60} draggable={false} />

        <h6 className={cn(typographyVariants({ variant: 'title-md' }))}>Susun koleksi bacaanmu</h6>

        <p className={cn(typographyVariants({ variant: 'body-lg' }))}>
          Susun koleksi bacaan yang berfokus pada tema atau tujuan pembelajaran tertentu. Berikan pengalaman membaca
          yang lebih terstruktur dan bermakna bagi {type === 'school' ? 'murid' : 'anak'}.
        </p>

        <Button onClick={() => setOpenMakeNewBookCollectionDialog(true)}>
          <PlusIcon className='srd:size-4 srd:mr-2' />
          Mulai susun koleksi baru
        </Button>
      </div>

      <Dialog open={openMakeNewBookCollectionDialog} onOpenChange={setOpenMakeNewBookCollectionDialog} modal>
        <DialogContent className='srd:w-full srd:max-w-[500px]'>
          <DialogHeader>
            <DialogTitle>Buat Koleksi</DialogTitle>

            <DialogDescription className='srd:sr-only'>
              Buat koleksi baru untuk memudahkan pencarian buku.
            </DialogDescription>
          </DialogHeader>

          <MyBookCollectionForm
            onFinishSubmitting={async ({ type, id }) => {
              setOpenMakeNewBookCollectionDialog(false)

              if (type === 'create') {
                toast.success('Koleksi berhasil dibuat!')
              } else {
                toast.success('Koleksi berhasil diperbarui!')
              }

              await queryClient.invalidateQueries({
                queryKey: trpc.myBookCollection.getAllBookCollections.infiniteQueryKey(),
              })

              await queryClient.invalidateQueries({
                queryKey: trpc.myBookCollection.getAllBookCollections.queryKey(),
              })

              await queryClient.invalidateQueries({
                queryKey: trpc.myBookCollection.getOneBookCollection.queryKey({ id }),
              })
            }}
            onCancel={() => setOpenMakeNewBookCollectionDialog(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  )
}
